import SubmitReview, { openReviewOf } from "../Reviews/SubmitReview";
import ReviewDetail from "../Reviews/ReviewDetail";
import React, { useEffect, useState } from "react";
import { Alert, Button, Dropdown, Modal } from "react-bootstrap";
import { MoreHorizontal } from "lucide-react";
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useWorkproductContext } from "../Context/WorkproductContext";
import DocumentAgentLG from "./DocumentAgentLG/DocumentAgentLG";
import { Question, describeJurisdiction, questionApi } from "../Questions/api";
import QuestionIntakeEditor from "../Questions/QuestionIntakeEditor";
import { Topic } from "../Questions/api";
import QuestionEngagement from "../Questions/QuestionEngagement";
import { customerStatusLabel, topicLabel } from "../Reviews/api";
import { customerWorkflow } from "../Reviews/customerWorkflow";
import WorkspaceHeader from "./Workspace/WorkspaceHeader";
import "./WorkproductPage.css";
import "./Workspace/workspace.css";

export default function WorkproductPage({
  workspaceId,
  question,
  topics = [],
  onQuestionUpdated,
  reviews,
  onReviewsChanged,
  reviewLoadError,
  onReviewUpdated,
  title,
  initialMessage,
  onInitialMessageConsumed,
}: {
  workspaceId?: string;
  question?: Question;
  topics?: Topic[];
  onQuestionUpdated?: (question: Question) => void;
  /** The question's attorney reviews, newest first. */
  reviews?: Question["reviews"];
  onReviewsChanged?: () => void;
  reviewLoadError?: string;
  onReviewUpdated?: (review: any) => void;
  /** Overrides the question's title in the header (e.g. a new AI title). */
  title?: string;
  initialMessage?: string;
  onInitialMessageConsumed?: () => void;
} = {}) {
  const navigate = useNavigate();
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [editingIntake, setEditingIntake] = useState(false);
  // A question with a review has two tabs: the AI chat and the attorney review.
  const [searchParams, setSearchParams] = useSearchParams();
  // A link can name an older review of this question; otherwise the newest.
  const latestReview =
    reviews?.find(r => r.id === searchParams.get("review")) || reviews?.[0];
  const openReview = reviews ? openReviewOf({ reviews }) : undefined;
  const tab =
    latestReview && searchParams.get("tab") === "attorney"
      ? "attorney"
      : "chat";
  const showTab = (next: "chat" | "attorney", reviewId?: string) =>
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        if (reviewId) params.set("review", reviewId);
        if (next === "attorney") params.set("tab", "attorney");
        else params.delete("tab");
        return params;
      },
      { replace: true }
    );
  const params = useParams<{ workproductid: string }>();
  const workproductid = workspaceId || params.workproductid;
  const { setWorkproductId, setWorkproduct, setFileContent, workproduct } =
    useWorkproductContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const questionId = question?.id;
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setWorkproduct(null);
    setFileContent(null);
    setWorkproductId(workproductid || null);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/file/${workproductid}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async response => {
        if (!response.ok)
          throw new Error(
            response.status === 404
              ? "Document not found."
              : "Could not open this document."
          );
        return response.json();
      })
      .then(data => {
        if (data.questionId && !questionId) {
          navigate(`/questions/${data.questionId}`, { replace: true });
          return;
        }
        if (data.workproduct?.type !== "TipTap Document Agent")
          throw new Error("This document type is not available in Reviewed.");
        setWorkproduct(data.workproduct);
        setFileContent(data.fileContent);
      })
      .catch(err => {
        if (!controller.signal.aborted) setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [
    workproductid,
    setWorkproductId,
    setWorkproduct,
    setFileContent,
    navigate,
    questionId,
    reload,
  ]);
  if (loading && !question)
    return (
      <div className="reviewed-loading" role="status">
        Opening document…
      </div>
    );
  if (error && !question)
    return (
      <main className="reviewed-documents">
        <p role="alert">{error}</p>
        <Button onClick={() => setReload(n => n + 1)}>Retry</Button>
        <Link className="ms-3" to="/documents">
          Back to documents
        </Link>
        <Link className="ms-3" to="/questions">
          My questions
        </Link>
      </main>
    );
  return (
    <main className="wp-workspace wp-workspace--immersive">
      {question ? (
        <header className="question-workspace-header">
          <Link to="/questions">← My questions</Link>
          <div className="question-workspace-title">
            <strong title={title || question.title}>
              {title || question.title}
            </strong>
            <small>
              {topicLabel(question.topicKey)} ·{" "}
              {describeJurisdiction(question.jurisdiction)} · AI answers aren’t
              checked by an attorney
            </small>
            {openReview && (
              <small>
                The current attorney review uses the topic and location saved
                when you submitted it.
              </small>
            )}
          </div>
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light"
              size="sm"
              className="question-workspace-menu"
              aria-label="Question options"
              disabled={archiving}
            >
              <MoreHorizontal size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {onQuestionUpdated && topics.length > 0 && (
                <Dropdown.Item onClick={() => setEditingIntake(true)}>
                  Edit topic and location
                </Dropdown.Item>
              )}
              <Dropdown.Item onClick={() => setShowTerms(true)}>
                Terms you accepted
              </Dropdown.Item>
              <Dropdown.Item
                onClick={async () => {
                  setArchiving(true);
                  setArchiveError("");
                  try {
                    await questionApi(`/${question.id}/status`, "PATCH", {
                      status: "archived",
                      version: question.version,
                    });
                    navigate("/questions");
                  } catch (e: any) {
                    setArchiveError(e.message);
                  } finally {
                    setArchiving(false);
                  }
                }}
              >
                Archive question
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Modal
            show={editingIntake}
            onHide={() => setEditingIntake(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Edit topic and location</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <QuestionIntakeEditor
                question={question}
                topics={topics}
                onSaved={q => {
                  onQuestionUpdated?.(q);
                  setEditingIntake(false);
                }}
                onReloaded={q => onQuestionUpdated?.(q)}
                onCancel={() => setEditingIntake(false)}
              />
            </Modal.Body>
          </Modal>
          <Modal show={showTerms} onHide={() => setShowTerms(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Terms you accepted</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <QuestionEngagement
                question={question}
                dirty={false}
                onActivated={async () => {}}
              />
            </Modal.Body>
          </Modal>
        </header>
      ) : (
        <WorkspaceHeader name={workproduct?.name} />
      )}
      {reviewLoadError && (
        <Alert variant="warning">
          {reviewLoadError}
          <Button variant="link" onClick={onReviewsChanged}>
            Retry review history
          </Button>
        </Alert>
      )}
      {archiveError && (
        <Alert variant="danger" dismissible onClose={() => setArchiveError("")}>
          {archiveError}
        </Alert>
      )}
      {question && latestReview && (
        <nav className="question-tabs" role="tablist" aria-label="Question">
          <button
            role="tab"
            id="question-chat-tab"
            tabIndex={tab === "chat" ? 0 : -1}
            onKeyDown={e => {
              if (["ArrowLeft", "ArrowRight", "End"].includes(e.key)) {
                e.preventDefault();
                showTab("attorney");
                document.getElementById("question-review-tab")?.focus();
              }
            }}
            aria-controls="question-chat-panel"
            aria-selected={tab === "chat"}
            onClick={() => showTab("chat")}
          >
            AI chat
          </button>
          <button
            role="tab"
            id="question-review-tab"
            tabIndex={tab === "attorney" ? 0 : -1}
            onKeyDown={e => {
              if (["ArrowLeft", "ArrowRight", "Home"].includes(e.key)) {
                e.preventDefault();
                showTab("chat");
                document.getElementById("question-chat-tab")?.focus();
              }
            }}
            aria-controls="question-review-panel"
            aria-selected={tab === "attorney"}
            onClick={() => showTab("attorney")}
          >
            Attorney review · {customerStatusLabel(latestReview)}
            {customerWorkflow(latestReview).needsAction && (
              <>
                <span className="question-tab-dot" aria-hidden="true" />
                <span className="visually-hidden">Needs your action</span>
              </>
            )}
          </button>
        </nav>
      )}
      {question && latestReview && (
        <div
          className="question-review-tab"
          id="question-review-panel"
          role="tabpanel"
          aria-labelledby="question-review-tab"
          hidden={tab !== "attorney"}
        >
          {tab === "attorney" && (
            <ReviewDetail
              key={latestReview.id}
              reviewId={latestReview.id}
              embedded
              onReviewLoaded={onReviewsChanged}
              onReviewChanged={onReviewUpdated}
              onBackToChat={() => {
                onReviewsChanged?.();
                showTab("chat");
              }}
            />
          )}
        </div>
      )}
      <div
        className="wp-workspace-body"
        id="question-chat-panel"
        role={question && latestReview ? "tabpanel" : undefined}
        aria-labelledby={
          question && latestReview ? "question-chat-tab" : undefined
        }
        style={tab === "attorney" ? { display: "none" } : undefined}
      >
        {loading ? (
          <p role="status">Opening AI chat…</p>
        ) : error ? (
          <section className="reviewed-documents">
            <Alert variant="warning">
              <h2 className="h5">We couldn’t open your AI chat</h2>
              <p>
                {latestReview
                  ? "Your attorney review and its submitted materials are still available."
                  : "Try again to reopen your conversation."}
              </p>
              <Button
                variant="outline-dark"
                onClick={() => setReload(n => n + 1)}
              >
                Retry AI chat
              </Button>
              {latestReview && (
                <Button className="ms-2" onClick={() => showTab("attorney")}>
                  View attorney review
                </Button>
              )}
            </Alert>
            <Link to="/questions">Back to my questions</Link>
          </section>
        ) : (
          <DocumentAgentLG
            initialMessage={initialMessage}
            onInitialMessageConsumed={onInitialMessageConsumed}
            extraInputActions={
              question
                ? ctx => (
                    <SubmitReview
                      question={question}
                      topics={topics}
                      onQuestionUpdated={onQuestionUpdated}
                      compact
                      reviewsReady={reviews !== undefined}
                      latestReviewId={reviews?.[0]?.id || null}
                      autoOpen={searchParams.get("requestReview") === "1"}
                      onAutoOpenConsumed={() =>
                        setSearchParams(
                          prev => {
                            const params = new URLSearchParams(prev);
                            params.delete("requestReview");
                            return params;
                          },
                          { replace: true }
                        )
                      }
                      {...ctx}
                      openReview={openReview}
                      onOpenReview={() => showTab("attorney", openReview?.id)}
                      onSubmitted={onReviewsChanged}
                    />
                  )
                : undefined
            }
            hideShareButton={Boolean(question)}
            consumerMode
            welcomeMessageOverride={
              question ? (
                <div>
                  <h3>Start with the decision you’re facing</h3>
                  <p>
                    Ask a question, paste an AI answer, or attach a document. AI
                    answers are free; when it matters, an attorney can check
                    them.
                  </p>
                </div>
              ) : (
                <div>
                  <h3>Prepare the documents behind your question</h3>
                  <p>
                    Write or upload a document and work through it with AI. For
                    attorney review, start a question and attach the relevant
                    files to that conversation.
                  </p>
                </div>
              )
            }
            documentPanelMode={question ? "auto" : "always"}
          />
        )}
      </div>
    </main>
  );
}
